<template>
  <div class="password-reset" redesign>
    <!-- Header -->
    <div class="local-hero">
      <h1 class="local-hero-header" v-translate>Password Reset</h1>
    </div>

    <!-- Password reset form -->
    <div v-if="!showSuccessMessage" class="form-container password-reset-form">
      <div class="form-header">
        <h4 v-translate>Choose Your New Password</h4>
      </div>

      <!-- Password restrictions -->
      <div class="info-container">
        <div class="tooltip-icon">
          <img src="@/assets/icons/information-icon.png" />
        </div>
        <div class="tooltip">
          <h4 class="tooltip-header" v-translate>
            Passwords must meet the following requirements:
          </h4>
          <span class="tooltip-text" v-translate>passwordRequirements</span>
        </div>
      </div>

      <div class="row-wrapper">
        <!-- Password -->
        <FormTextField
          id="newPassword1"
          type="password"
          label="Password"
          :tabIndex="1"
          :value.sync="newPassword1"
          :showErrorMessage="hasSubmitted && isNotValidPassword"
          errorMessage="Your password does not meet the password requirements outlined above."
          required
        />

        <!-- Confirm password -->
        <FormTextField
          id="newPassword2"
          type="password"
          label="Re-enter password"
          :tabIndex="2"
          :value.sync="newPassword2"
          :showErrorMessage="hasSubmitted && !this.passwordsMatch"
          :errorMessage="$gettext('Must match')"
          required
        />
      </div>

      <div v-if="invalidToken" class="error-container">
        <div class="tooltip-icon">
          <img src="@/assets/icons/error-icon.png" />
        </div>
        <div class="tooltip">
          <h4 class="tooltip-header" v-translate>Invalid Token</h4>
          <div class="tooltip-text">
            <translate>Your password reset token has already been used, or has expired.</translate>
            <p />
            <translate>Please request a new password reset token</translate>
            <a href="/password/reset" v-translate>here</a>.
          </div>
        </div>
      </div>

      <div class="button-container">
        <button class="form-button" @click="handleSubmit">
          <span v-if="!loading" key="next" v-translate>Submit</span>
          <span v-else key="spinner" disabled>
            <LoadSpinner :color="'white'" />
          </span>
        </button>
      </div>
    </div>

    <!-- Password reset success message -->
    <div v-else class="form-container password-reset-form">
      <div class="form-header card-shape">
        <div class="success-circle-check-icon">
          <img src="@/assets/icons/success-circle-check-icon.png" />
        </div>
        <h4 v-translate>Success!</h4>
        <p v-translate>Password has been reset.</p>
      </div>
      <div class="button-container">
        <router-link :to="{ name: 'Login' }">
          <md-button class="form-button">
            <span v-translate>Back to login</span>
          </md-button>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import LoadSpinner from '../components/LoadSpinner'
import User from '@/services/users'
import { analyticsMixin } from '@/services/googleAnalytics'
import FormTextField from '@/components/forms/FormTextField'

export default {
  name: 'PasswordResetConfirm',
  mixins: [analyticsMixin],
  components: {
    LoadSpinner,
    FormTextField,
  },
  data() {
    return {
      newPassword1: '',
      newPassword2: '',
      loading: false,
      showSuccessMessage: false,
      hasSubmitted: false,
      invalidToken: false,
    }
  },
  created() {
    this.trackCustomPageview('PasswordResetConfirm', '/password/reset/confirm')
  },
  methods: {
    handleSubmit() {
      if (this.loading) {
        return null // if this line is reached, then the user clicked the button before receiving response -- trying to double submit
      }
      this.loading = true
      this.hasSubmitted = true

      if (this.isNotValidPassword || !this.newPassword1) {
        this.loading = false
        document.getElementById('newPassword1').focus()
        return
      } else if (!this.passwordsMatch || !this.newPassword2.length) {
        this.loading = false
        document.getElementById('newPassword2').focus()
        return
      }

      let { uid, token } = this.$route.params
      User.api
        .passwordResetConfirm(this.newPassword1, this.newPassword2, uid, token)
        .then(() => {
          this.trackCustomPageview('PasswordResetSuccess', '/password/reset/success')
          this.showSuccessMessage = true
        })
        .catch(error => {
          this.invalidToken = true
          this.loading = false
        })
        .finally(() => {
          this.newPassword1 = ''
          this.newPassword2 = ''
          this.loading = false
        })
    },
  },
  computed: {
    ...mapState(['organizationCssVars']),
    isNotValidPassword() {
      let regex_pass = new RegExp(
        '(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[~!@#$%^&;*()_+{}":])(?=.{10,})',
      )

      return this.newPassword1.length > 0 && !regex_pass.test(this.newPassword1)
    },
    passwordsMatch() {
      return this.newPassword1 == this.newPassword2
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/responsive';

.password-reset {
  display: flex;
  flex-direction: column;

  .password-reset-form {
    margin: 2rem;

    @extend .constrain-tablet-and-up;

    .row-wrapper {
      margin-top: 0;
    }

    .success-circle-check-icon {
      height: 32px;
      width: 32px;
      margin-bottom: 1rem;
    }

    .card-shape {
      width: 100%;
      align-self: center;
      max-width: 500px;
    }
  }
}

/*
.ruby-font {
  color: $ruby;
}

.page-content {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100vh;
  padding: 0 20px 50px 20px;
}

.success {
  display: flex;
  flex-flow: column;
  align-items: center;

  p {
    text-align: center;
    margin-bottom: 1vh;
  }
}

.form {
  display: flex;
  flex-flow: column;
  align-items: center;
}

.password-field {
  width: 17rem;
}

.button {
  @include primary-button(var(--dark-brand-color));
}

#errors {
  color: $ruby;
  font-weight: 600;
  font-size: 12px;
  display: flex;
  flex-flow: column;
  align-items: center;
}

ul {
  margin-top: 2vh;
  margin-bottom: 1vh;
}
*/
</style>
